/**
 * CampusPoll: bloque de encuesta dentro de un post.
 * Extraído para reducir la profundidad de JSX en CampusPostCard.jsx.
 */
import React from 'react';
import { Loader2, Check, Lock, Eye, BarChart2, Edit } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function CampusPoll({ post, ctx }) {
  const enc = post.encuesta;
  const res = post.encuesta_resultados || {
    conteo: {}, total_votantes: 0, mi_voto: [], cerrada: false, votantes: null,
  };
  const cerrada = res.cerrada;
  const total = res.total_votantes || 0;
  const seleccionActual = ctx.pollSelection[post.id];
  const yaVoto = (res.mi_voto || []).length > 0;
  const usandoSeleccionLocal = seleccionActual !== undefined;
  const seleccion = usandoSeleccionLocal ? seleccionActual : (res.mi_voto || []);
  const cambio = usandoSeleccionLocal
    && JSON.stringify([...seleccion].sort()) !== JSON.stringify([...(res.mi_voto || [])].sort());
  const puedeVotar = !cerrada && seleccion.length > 0 && (!yaVoto || cambio);

  return (
    <div className="mt-3 space-y-2" data-testid={`encuesta-block-${post.id}`}>
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <BarChart2 className="w-3.5 h-3.5" />
          {enc.multi ? 'Múltiples respuestas' : 'Una sola respuesta'}
        </span>
        <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Pública</span>
        {cerrada && (
          <Badge variant="outline" className="text-rose-600 border-rose-300">
            <Lock className="w-3 h-3 mr-1" /> Cerrada
          </Badge>
        )}
        {!cerrada && enc.cierre && (
          <span className="text-gray-400">Cierra: {ctx.formatFecha(enc.cierre)}</span>
        )}
      </div>

      <div className="space-y-2">
        {(enc.opciones || []).map((op) => (
          <PollOption key={op.id} op={op} post={post} ctx={ctx}
            enc={enc} res={res} cerrada={cerrada} total={total} seleccion={seleccion} />
        ))}
      </div>

      <div className="flex items-center justify-between pt-1">
        <span className="text-xs text-gray-500">
          {total} {total === 1 ? 'voto' : 'votos'}
        </span>
        <div className="flex gap-2">
          {puedeVotar && (
            <Button size="sm" onClick={() => ctx.votarEncuesta(post.id)}
              disabled={ctx.votingPost[post.id]} data-testid={`votar-btn-${post.id}`}>
              {ctx.votingPost[post.id]
                ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                : <Check className="w-3.5 h-3.5 mr-1" />}
              {yaVoto ? 'Cambiar voto' : 'Votar'}
            </Button>
          )}
          {ctx.esCoordinadorODocente && (
            <>
              <Button size="sm" variant="outline" onClick={() => ctx.abrirEdicionEncuesta(post)}
                data-testid={`editar-encuesta-${post.id}`}>
                <Edit className="w-3.5 h-3.5 mr-1" /> Editar
              </Button>
              <Button size="sm" variant="outline" onClick={() => ctx.cerrarEncuesta(post.id, cerrada)}
                data-testid={`cerrar-encuesta-${post.id}`}>
                {cerrada ? 'Reabrir' : 'Cerrar'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PollOption({ op, post, ctx, enc, res, cerrada, total, seleccion }) {
  const conteoOp = res.conteo?.[op.id] || 0;
  const pct = total > 0 ? Math.round((conteoOp / total) * 100) : 0;
  const elegida = seleccion.includes(op.id);
  const fueMiVoto = (res.mi_voto || []).includes(op.id);
  const votantes = res.votantes?.[op.id] || [];
  const expanded = ctx.showVoters[`${post.id}::${op.id}`];

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => !cerrada && ctx.togglePollSelection(post, op.id)}
        disabled={cerrada}
        className={`w-full text-left relative overflow-hidden rounded-md border transition-colors ${
          elegida ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-violet-300 hover:bg-violet-50/40'
        } ${cerrada ? 'cursor-default opacity-90' : 'cursor-pointer'}`}
        data-testid={`encuesta-opcion-btn-${post.id}-${op.id}`}
      >
        <div
          className={`absolute inset-y-0 left-0 ${elegida ? 'bg-violet-200/60' : 'bg-gray-100'}`}
          style={{ width: `${pct}%` }}
        />
        <div className="relative flex items-center justify-between px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            <span className={`w-4 h-4 rounded-${enc.multi ? 'sm' : 'full'} border-2 flex items-center justify-center ${
              elegida ? 'border-violet-600 bg-violet-600' : 'border-gray-300'
            }`}>
              {elegida && <Check className="w-2.5 h-2.5 text-white" />}
            </span>
            <span className={fueMiVoto ? 'font-semibold' : ''}>{op.texto}</span>
          </div>
          <span className="text-xs font-medium text-gray-600 tabular-nums">{conteoOp} · {pct}%</span>
        </div>
      </button>
      {votantes.length > 0 && (
        <div className="pl-3">
          <button
            type="button"
            onClick={() => ctx.setShowVoters((s) => ({ ...s, [`${post.id}::${op.id}`]: !expanded }))}
            className="text-xs text-violet-600 hover:underline"
          >
            {expanded ? 'Ocultar' : 'Ver'} votantes ({votantes.length})
          </button>
          {expanded && (
            <div className="text-xs text-gray-600 mt-0.5">
              {votantes.map((v) => `${v.nombre || ''} ${v.apellido || ''}`.trim()).filter(Boolean).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
